import { useState, useEffect } from 'react';
import type { GraphQLTaggedNode } from 'relay-runtime';

import { expVal } from '@confluence/feature-experiments';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import {
	useQueryInterop,
	type UseQueryInteropSupportedResult,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

type FeedDropdownStorageConfig<TValue> = {
	defaultValue: TValue;
	sessionStorageKey: string;
	skipQuery?: boolean;
	interopApolloAggClient?: boolean;
};

type FeedDropdownQueryConfig<TVariables, TValue> = {
	query: GraphQLTaggedNode;
	variables: (value: TValue) => Omit<TVariables, 'first'>;
};

export function useFeedDropdownStorage<TData, TVariables extends Record<string, any>, TValue>(
	config: FeedDropdownStorageConfig<TValue>,
	queryConfig: FeedDropdownQueryConfig<TVariables, TValue>,
): {
	value: TValue;
	setValue: (newValue: TValue) => void;
	query: UseQueryInteropSupportedResult<TData, TVariables>;
} {
	const { defaultValue, sessionStorageKey, skipQuery, interopApolloAggClient } = config;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = process.env.REACT_SSR || window.__SSR_RENDERED__;

	// Initialize state with session storage or default
	const [value, setValue] = useState((): TValue => {
		if (!isSSR) {
			const sessionValue = sessionStorage.getItem(sessionStorageKey);
			if (sessionValue && sessionValue !== defaultValue) {
				return sessionValue as TValue;
			}
		}
		return defaultValue;
	});

	const isFollowingFeed = defaultValue === 'relevant';

	const initialPageSize = isFollowingFeed
		? expVal('confluence_home_feed_initial_page_size', 'following_spa', 5)
		: 10;

	const query = useQueryInterop<TData, TVariables>(queryConfig.query, {
		variables: {
			first: initialPageSize,
			...queryConfig.variables(value),
		} as unknown as TVariables,
		context: { single: true },
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
		skip: skipQuery,
		interopApolloAggClient,
	});

	useEffect(() => {
		sessionStorage.setItem(sessionStorageKey, value as string);
	}, [value, sessionStorageKey]);

	return { value, setValue, query };
}
